import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useReduxActions } from 'hooks/useReduxActions';
import { useTranslation } from 'react-i18next';
import { MdClose } from 'react-icons/md';
import { AiOutlineCaretDown } from 'react-icons/ai';
import { colors, ROLES } from '../../../../constants';
import { employeesSelector, userSelector } from '../../../../store/selectors';
import ModalContainer from 'components/styled/ModalContainer';
import Button from 'components/Button';
import Text from 'components/styled/Text';
import DropDown from 'components/DropDown';
import Avatar from 'components/Avatar';

const EmployeeSelectModalContainer = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: 435px;
  box-shadow: 0px 6px 12px 0px ${colors.navy}12;
`;

const StyledHeader = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid ${colors.grey95};
`;

const TitleContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`;

const RoleSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
`;

const Role = styled.div`
  display: flex;
  align-items: center;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px;
  border-top: 1px solid ${colors.grey95};
`;

const roles = [
  ROLES.SPECIALIST,
  ROLES.EXPERT,
  ROLES.MENTOR,
  ROLES.MASTER,
  ROLES.HELPER,
  ROLES.AGENT,
];

const EmployeeSelectModal = ({ onClose, ID, team }) => {
  const { t } = useTranslation();
  const employees = useSelector(employeesSelector);
  const user = useSelector(userSelector);
  const actions = useReduxActions();
  const [newTeam, setNewTeam] = useState(team);
  const [isOpenDropDown, setIsOpenDropDown] = useState({});

  const getFilteredEmployees = role => employee => {
    if (employee.role === ROLES.FOUNDER) {
      return false;
    }
    switch (role) {
      case ROLES.SPECIALIST:
        return (
          employee.role !== ROLES.AGENT &&
          employee.role !== ROLES.HELPER &&
          !(
            newTeam[ROLES.EXPERT]?._id === employee._id &&
            newTeam[ROLES.MENTOR]?._id === employee._id
          )
        );
      case ROLES.EXPERT:
        return (
          employee.role !== ROLES.SPECIALIST &&
          employee.role !== ROLES.AGENT &&
          employee.role !== ROLES.HELPER &&
          !(
            newTeam[ROLES.SPECIALIST]?._id === employee._id &&
            newTeam[ROLES.MENTOR]?._id === employee._id
          )
        );
      case ROLES.MENTOR:
        return (
          employee.role !== ROLES.SPECIALIST &&
          employee.role !== ROLES.EXPERT &&
          employee.role !== ROLES.AGENT &&
          employee.role !== ROLES.HELPER &&
          !(
            newTeam[ROLES.SPECIALIST]?._id === employee._id &&
            newTeam[ROLES.EXPERT]?._id === employee._id
          )
        );
      case ROLES.MASTER:
        return employee.role === ROLES.MASTER;
      case ROLES.HELPER:
        return employee.role === ROLES.HELPER;
      case ROLES.AGENT:
        return true;
      default:
        return false;
    }
  };

  const handleClick = () => {
    const users = Object.values(newTeam).map((user, index) => ({
      userRole: Object.keys(newTeam)[index],
      userId: user._id,
    }));
    actions.assignEmployees(ID, users);
  };

  const handleSelectEmployee = (employee, role) => () => {
    setNewTeam({ ...newTeam, [role]: employee });
    setIsOpenDropDown({ ...isOpenDropDown, [role]: false });
  };

  const handleOpenDropDown = role => () => {
    setIsOpenDropDown({ ...isOpenDropDown, [role]: true });
  };

  const handleCloseDropDown = role => event => {
    event.stopPropagation();
    setIsOpenDropDown({ ...isOpenDropDown, [role]: false });
  };

  return createPortal(
    <ModalContainer>
      <EmployeeSelectModalContainer>
        <StyledHeader>
          <TitleContainer>
            <Text variant="title2">{t('employee.select.modal.team')}</Text>
            <Text variant="body" color={colors.grey40}>
              {ID}
            </Text>
          </TitleContainer>
          <MdClose onClick={onClose} fill={colors.navy} />
        </StyledHeader>
        <StyledBody>
          {roles.map((role, index) => {
            return (
              !(user.role === ROLES.MENTOR && role === ROLES.MASTER) && (
                <RoleSelect key={role}>
                  <Text>{t(`employee.select.modal.${role}`)}</Text>
                  <DropDown
                    onOutsideClick={handleCloseDropDown(role)}
                    direction={index > 3 ? 'bottom' : 'top'}
                    show={!!isOpenDropDown[role]}
                    items={employees
                      .filter(getFilteredEmployees(role))
                      .map(employee => {
                        return {
                          icon: (
                            <Avatar
                              size="medium"
                              image={employee.profile?.avatar?.url}
                            />
                          ),
                          text: `${employee.profile?.first_name} ${employee.profile?.last_name}`,
                          onClick: handleSelectEmployee(employee, role),
                        };
                      })}>
                    <Role onClick={handleOpenDropDown(role)}>
                      {newTeam[role] ? (
                        <>
                          <Avatar
                            size="medium"
                            image={newTeam[role]?.profile?.avatar?.url}
                          />
                          <Text variant="body-small">{`${newTeam[role]?.profile?.first_name} ${newTeam[role]?.profile?.last_name}`}</Text>
                        </>
                      ) : (
                        <Text>
                          {t('employee.select.modal.select.employee')}{' '}
                          <AiOutlineCaretDown style={{ height: '18px' }} />
                        </Text>
                      )}
                    </Role>
                  </DropDown>
                </RoleSelect>
              )
            );
          })}
        </StyledBody>
        <StyledFooter>
          <Button onClick={handleClick} size="large" variant="primary">
            {t('employee.select.modal.add.button')}
          </Button>
        </StyledFooter>
      </EmployeeSelectModalContainer>
    </ModalContainer>,
    document.body,
  );
};

EmployeeSelectModal.propTypes = {
  ID: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  team: PropTypes.object,
};

EmployeeSelectModal.defaultProps = {
  team: {},
};

export default EmployeeSelectModal;
